'use client';

import { useEffect, useState } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

/**
 * Auto-generated "On this page" rail. Scans the nearest <article> for
 * h2/h3 elements that carry an id and tracks the active section on scroll.
 */
export default function OnThisPage() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [active, setActive] = useState<string>('');

  useEffect(() => {
    const article = document.querySelector('article');
    if (!article) return;

    const nodes = Array.from(article.querySelectorAll<HTMLElement>('h2[id], h3[id]'));
    setHeadings(
      nodes.map((el) => ({
        id: el.id,
        text: el.textContent ?? '',
        level: el.tagName === 'H3' ? 3 : 2,
      })),
    );

    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.boundingClientRect.top);
          else visible.delete(entry.target.id);
        }
        if (visible.size > 0) {
          const topmost = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
          setActive(topmost[0]);
        }
      },
      { rootMargin: '-120px 0px -70% 0px', threshold: 0 },
    );

    nodes.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  if (headings.length < 2) return null;

  return (
    <aside className="hidden xl:block">
      <div className="sticky top-28">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">On this page</p>
        <ul className="space-y-1.5 border-l border-neutral-800/60">
          {headings.map((h) => (
            <li key={h.id} style={{ paddingLeft: h.level === 3 ? '1.5rem' : '0.75rem' }}>
              <a
                href={`#${h.id}`}
                className={`-ml-px block border-l-2 py-0.5 text-[13px] transition-colors ${
                  active === h.id
                    ? 'border-rose-500 text-rose-400'
                    : 'border-transparent text-neutral-500 hover:text-neutral-300'
                }`}
                style={{ paddingLeft: '0.75rem' }}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
